/***
 *
 *   GOOGLE DRIVE PICKER MODAL
 *   Lists CSV files from the Google Drive folder linked to the order
 *   and attaches the chosen file to the order item as its data file.
 *
 **********/

import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Icon, Loader } from "components/lib";
import axios from "axios";

export default function GoogleDrivePickerModal({ open, setOpen, orderItemId, onSelected, onPick, nameFilter }) {

    const [items, setItems] = useState(null);
    const [error, setError] = useState(null);
    const [selectingId, setSelectingId] = useState(null);
    const [search, setSearch] = useState('');

    // breadcrumb trail of folders navigated into (empty = order root folder)
    const [folderStack, setFolderStack] = useState([]);

    function loadFolder(folderId) {

        setItems(null);
        setError(null);
        setSearch('');

        axios.get('/api/driveFile/' + orderItemId + '/list' + (folderId ? '?folderId=' + folderId : ''))
            .then(res => setItems(res.data.data))
            .catch(err => setError(err.response?.data?.message || 'Failed to load files from Google Drive.'));
    }

    // load the order root folder every time the modal opens
    useEffect(() => {

        if (!open) return;

        setSelectingId(null);
        setFolderStack([]);
        loadFolder(null);

    }, [open, orderItemId]);

    function openFolder(folder) {

        setFolderStack(prev => [...prev, { id: folder.id, name: folder.name }]);
        loadFolder(folder.id);
    }

    // navigate via breadcrumb: index -1 = root, otherwise into the stack
    function goToBreadcrumb(index) {

        const newStack = index < 0 ? [] : folderStack.slice(0, index + 1);
        setFolderStack(newStack);
        loadFolder(newStack.length ? newStack[newStack.length - 1].id : null);
    }

    const isFolder = (f) => f.mimeType === 'application/vnd.google-apps.folder';

    // folders always shown; the name filter (e.g. FOR_FACTORY) applies to files only
    const folders = (items || []).filter(f => isFolder(f) &&
        f.name.toLowerCase().includes(search.trim().toLowerCase()));

    // CSV filtering happens here by extension (Drive-side filtering is unreliable)
    const files = items === null ? null : items.filter(f => !isFolder(f) &&
        f.name.toLowerCase().endsWith('.csv') &&
        (!nameFilter || f.name.toLowerCase().includes(nameFilter.toLowerCase())));

    async function selectFile(file) {

        try {

            setSelectingId(file.id);

            // custom handler (e.g. import realized data); otherwise the
            // default behaviour attaches the file as the item's data file
            if (onPick) {
                await onPick(file);
            }
            else {
                await axios.post('/api/driveFile/' + orderItemId + '/select', { fileId: file.id });
            }

            setOpen(false);
            if (onSelected) onSelected(file);

        } catch (err) {

            setSelectingId(null);
            setError(err.response?.data?.message || 'Failed to select file.');
        }
    }

    function formatSize(bytes) {

        if (!bytes) return '';
        const kb = bytes / 1024;
        return kb > 1024 ? (kb / 1024).toFixed(1) + ' MB' : Math.max(1, Math.round(kb)) + ' KB';
    }

    function formatDate(iso) {

        if (!iso) return '';
        return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    }

    const visibleFiles = (files || []).filter(f =>
        f.name.toLowerCase().includes(search.trim().toLowerCase())
    );

    return (
        <Transition.Root show={open} as={Fragment}>
            <Dialog
                as="div"
                className="fixed z-10 inset-0 overflow-y-auto"
                onClose={() => !selectingId && setOpen(false)}
            >
                <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">

                    {/* Overlay */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>

                    {/* Centering trick */}
                    <span
                        className="hidden sm:inline-block sm:align-middle sm:h-screen"
                        aria-hidden="true"
                    >
            &#8203;
          </span>

                    {/* Modal Panel */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <div className="relative inline-block align-bottom bg-white rounded-xl text-left overflow-hidden shadow-xl transform transition-all mt-12 sm:mt-16 sm:mb-8 sm:align-middle sm:max-w-lg sm:w-full">

                            {/* Header */}
                            <div className="px-6 pt-6 pb-4 border-b border-gray-100">
                                <div className="flex items-center gap-3">
                                    <div className="flex items-center justify-center h-10 w-10 rounded-full bg-orange-100">
                                        <Icon image="hard-drive" size={20} color="orange" />
                                    </div>
                                    <div>
                                        <Dialog.Title className="text-lg font-semibold text-gray-900 leading-6">
                                            Choose File From Google Drive
                                        </Dialog.Title>
                                        <p className="text-xs text-gray-400 mt-0.5">
                                            CSV files from this order's Drive folder
                                        </p>
                                    </div>
                                </div>

                                {/* Breadcrumbs */}
                                <div className="flex items-center flex-wrap gap-1 mt-4 text-xs">
                                    <span
                                        onClick={() => !selectingId && goToBreadcrumb(-1)}
                                        className={folderStack.length ? 'text-orange-600 hover:underline cursor-pointer' : 'text-gray-500 font-medium'}
                                    >
                                        Order Folder
                                    </span>
                                    { folderStack.map((crumb, index) => (
                                        <Fragment key={crumb.id}>
                                            <span className="text-gray-300">/</span>
                                            <span
                                                onClick={() => !selectingId && index < folderStack.length - 1 && goToBreadcrumb(index)}
                                                className={index < folderStack.length - 1 ? 'text-orange-600 hover:underline cursor-pointer' : 'text-gray-500 font-medium'}
                                            >
                                                {crumb.name}
                                            </span>
                                        </Fragment>
                                    )) }
                                </div>

                                {/* Search */}
                                { items && items.length > 0 &&
                                    <div className="relative mt-3">
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                            <Icon image="search" size={14} color="grey" />
                                        </span>
                                        <input
                                            type="text"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            placeholder="Search files..."
                                            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent placeholder-gray-400"
                                        />
                                    </div> }
                            </div>

                            {/* Body */}
                            <div className="px-3 py-2 max-h-80 overflow-y-auto">

                                { error &&
                                    <div className="flex items-start gap-2 m-3 p-3 rounded-lg bg-red-50">
                                        <Icon image="alert-triangle" size={16} color="red" />
                                        <p className="text-sm text-red-600 leading-relaxed">{error}</p>
                                    </div> }

                                { !items && !error &&
                                    <div className="flex flex-col items-center justify-center py-10 gap-3">
                                        <Loader classNames="w-6 h-6" />
                                        <p className="text-xs text-gray-400">Loading files from Google Drive...</p>
                                    </div> }

                                { files && files.length === 0 && folders.length === 0 &&
                                    <div className="flex flex-col items-center justify-center py-10 gap-2 text-center">
                                        <Icon image="folder" size={24} color="grey" />
                                        <p className="text-sm text-gray-500">
                                            { nameFilter ? `No CSV files containing "${nameFilter}" found` : 'No CSV files found' }
                                        </p>
                                        <p className="text-xs text-gray-400">Add CSV files to this folder or navigate into another one.</p>
                                    </div> }

                                { files && (files.length > 0 || folders.length > 0) && visibleFiles.length === 0 && folders.length === 0 &&
                                    <p className="text-sm text-gray-500 text-center py-8">
                                        No files match "{search}"
                                    </p> }

                                {/* Folders (click to navigate) */}
                                { folders.map(folder => (
                                    <div
                                        key={folder.id}
                                        onClick={() => !selectingId && openFolder(folder)}
                                        className="group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors duration-100 border border-transparent hover:bg-orange-50 hover:border-orange-100"
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-orange-50 shrink-0">
                                                <Icon image="folder" size={16} color="orange" />
                                            </div>
                                            <p className="text-sm font-medium text-gray-800 truncate group-hover:whitespace-normal group-hover:break-all">{folder.name}</p>
                                        </div>
                                        <div className="flex items-center shrink-0">
                                            <Icon image="chevron-right" size={16} color="grey" />
                                        </div>
                                    </div>
                                )) }

                                { visibleFiles.map(file => (
                                    <div
                                        key={file.id}
                                        onClick={() => !selectingId && selectFile(file)}
                                        className={`group flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer transition-colors duration-100 border
                                            ${selectingId === file.id
                                                ? 'bg-orange-50 border-orange-200'
                                                : 'border-transparent hover:bg-orange-50 hover:border-orange-100'}
                                            ${selectingId && selectingId !== file.id ? 'opacity-40 pointer-events-none' : ''}`}
                                    >
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-green-50 shrink-0">
                                                <Icon image="file-text" size={16} color="green" />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-sm font-medium text-gray-800 truncate group-hover:whitespace-normal group-hover:break-all">{file.name}</p>
                                                <p className="text-xs text-gray-400">
                                                    {[formatSize(file.size), formatDate(file.modifiedTime)].filter(Boolean).join(' · ')}
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-center shrink-0">
                                            { selectingId === file.id
                                                ? <Loader classNames="w-4 h-4 inline-block" />
                                                : <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-100 text-xs font-medium text-orange-600">
                                                    Select
                                                  </span> }
                                        </div>
                                    </div>
                                )) }

                            </div>

                            {/* Footer */}
                            <div className="px-6 py-4 border-t border-gray-100 bg-gray-50 flex justify-between items-center">

                                <span className="text-xs text-gray-400">
                                    { files ? `${visibleFiles.length} file${visibleFiles.length === 1 ? '' : 's'}` : '' }
                                </span>

                                <button
                                    type="button"
                                    onClick={() => setOpen(false)}
                                    disabled={!!selectingId}
                                    className="text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-150"
                                >
                                    Cancel
                                </button>

                            </div>

                        </div>
                    </Transition.Child>
                </div>
            </Dialog>
        </Transition.Root>
    );
}
